/*
Blur overlay + answer queue shown over the EMR page while Comprehend fills it.

Same look, ids and messages as the copies in NewWebPTSoap2Inject.ts and
NewRaintreeInject.ts (those keep their own; this one is for new injectors).
Show/hide is ref-counted so overlapping answers don't hide each other's overlay.
*/

const OVERLAY_ID = 'BlurOverlay';
const ACTIVE_CLASS = 'blur-overlay-active';
const CSS_ID = 'BlurOverlayStyles';

let overlayEl: HTMLElement | null = null;
let msgEl: HTMLElement | null = null;
let closeBtn: HTMLButtonElement | null = null;
let lastFocused: HTMLElement | null = null;

function injectStyles() {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = `
     html.${ACTIVE_CLASS} { overflow: hidden; }
     body.${ACTIVE_CLASS} { overflow: hidden; }
     html.${ACTIVE_CLASS} body > *:not(#${OVERLAY_ID}) {
       filter: blur(var(--blur-overlay-amount, 8px)) saturate(0.9);
       pointer-events: none;
       user-select: none;
     }
     #${OVERLAY_ID} {
       position: fixed;
       inset: 0;
       --brand-color: rgb(107, 216, 136);
       z-index: 2147483647;
       display: none;
       align-items: center;
       justify-content: center;
       padding: 24px;
     }
     #${OVERLAY_ID}[data-open="true"] { display: flex; }
     #${OVERLAY_ID}::before {
       content: "";
       position: absolute;
       inset: 0;
       background: rgba(0,0,0,0.25);
       backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
       -webkit-backdrop-filter: blur(var(--blur-overlay-amount, 8px)) brightness(0.95);
     }
     #${OVERLAY_ID} .blur-msg {
       position: relative;
       max-width: min(720px, 90vw);
       width: max(320px, 40vw);
       background: rgba(255,255,255,0.9);
       border-radius: 16px;
       box-shadow: 0 10px 30px rgba(0,0,0,0.2);
       padding: 20px 24px;
       font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
       color: #111;
       backdrop-filter: blur(2px);
     }
     #${OVERLAY_ID} .blur-msg h2 {
       margin: 0 0 10px 0;
       font-size: 18px;
       font-weight: 600;
       color: var(--brand-color);
     }
     #${OVERLAY_ID} .blur-msg .blur-text {
       line-height: 1.5;
       font-size: 15px;
       white-space: pre-wrap;
     }
     #${OVERLAY_ID} .blur-close {
       position: fixed;
       top: 12px;
       right: 12px;
       appearance: none;
       border: none;
       background: var(--brand-color);
       color: #fff;
       width: 36px;
       height: 36px;
       border-radius: 10px;
       font-size: 20px;
       line-height: 36px;
       text-align: center;
       cursor: pointer;
       box-shadow: 0 4px 12px rgba(0,0,0,0.25);
     }
     #${OVERLAY_ID} .blur-close:hover { background: #00937f; }
     #${OVERLAY_ID} .blur-close:focus { outline: 2px solid #fff; outline-offset: 2px; }
   `;
  document.head.appendChild(style);
}

function createOverlay() {
  const existing = document.getElementById(OVERLAY_ID);
  if (existing) {
    overlayEl = existing;
    msgEl = existing.querySelector('.blur-text');
    closeBtn = existing.querySelector('.blur-close');
    return;
  }
  overlayEl = document.createElement('div');
  overlayEl.id = OVERLAY_ID;
  overlayEl.setAttribute('aria-hidden', 'true');

  const card = document.createElement('div');
  card.className = 'blur-msg';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('h2');
  title.textContent = 'ComprehendPT';
  msgEl = document.createElement('div');
  msgEl.className = 'blur-text';

  closeBtn = document.createElement('button');
  closeBtn.className = 'blur-close';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close overlay');
  closeBtn.textContent = '×';

  card.appendChild(title);
  card.appendChild(msgEl);
  overlayEl.appendChild(card);
  overlayEl.appendChild(closeBtn);
  document.body.appendChild(overlayEl);

  // X / Escape only; clicks on the dimmed page do nothing.
  closeBtn.addEventListener('click', hide);
  document.addEventListener('keydown', (e) => {
    if (overlayEl?.dataset.open === 'true' && e.key === 'Escape') hide();
  });
}

function show(message: string) {
  injectStyles();
  createOverlay();
  msgEl.textContent = message;
  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.classList.add(ACTIVE_CLASS);
  overlayEl.dataset.open = 'true';
  overlayEl.setAttribute('aria-hidden', 'false');
  lastFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  closeBtn.focus();
}

function update(message: string) {
  if (!overlayEl?.isConnected) createOverlay();
  msgEl.textContent = message;
}

function hide() {
  if (!overlayEl) return;
  overlayEl.dataset.open = 'false';
  overlayEl.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove(ACTIVE_CLASS);
  document.body.classList.remove(ACTIVE_CLASS);
  if (lastFocused && document.contains(lastFocused)) {
    try {
      lastFocused.focus();
    } catch {}
  }
  lastFocused = null;
}

function isVisible() {
  // The page may have replaced the body under us; a detached overlay isn't shown.
  return !!overlayEl?.isConnected && overlayEl.dataset.open === 'true';
}

let refCount = 0;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
// Fills take well under a second and answers arrive one by one, so an instant
// hide flashes the overlay per answer. Linger to bridge the gaps.
const HIDE_LINGER_MS = 1500;

export function beginOverlay(message: string) {
  refCount++;
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (isVisible()) update(message);
  else show(message);
}

export function endOverlay() {
  refCount = Math.max(0, refCount - 1);
  if (refCount > 0 || !isVisible() || hideTimer) return;
  hideTimer = setTimeout(() => {
    hideTimer = null;
    if (refCount === 0) hide();
  }, HIDE_LINGER_MS);
}

// Queue of items waiting to be filled; the first one is being filled now.
const queue: string[] = [];
let currentDetail = '';

function queueMessage() {
  const header = 'Finishing your note… This should only take a few seconds.';
  if (!queue.length) return header;
  const current = currentDetail ? `${queue[0]} — ${currentDetail}` : queue[0];
  const next = queue.slice(1);
  const preview = next.slice(0, 3);
  const remaining = next.length - preview.length;
  let message = `${header}\nCurrently working on: ${current}`;
  if (preview.length) {
    message +=
      `\nUp next: ${preview.join(', ')}` +
      (remaining > 0 ? `, and ${remaining} more` : '');
  }
  return message;
}

export function pushToQueue(label: string) {
  if (!label || queue.includes(label)) return;
  queue.push(label);
  if (isVisible()) update(queueMessage());
}

export function removeFromQueue(label: string) {
  const idx = queue.indexOf(label);
  if (idx !== -1) queue.splice(idx, 1);
  if (idx === 0) currentDetail = '';
  if (isVisible()) update(queueMessage());
}

/** Sub-step of the item being filled now, e.g. the section within a tab. */
export function setCurrentDetail(detail: string) {
  currentDetail = detail;
  if (isVisible()) update(queueMessage());
}

/** Clear counts and queue left by an interrupted fill. */
export function resetOverlay() {
  refCount = 0;
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  queue.length = 0;
  currentDetail = '';
  if (isVisible()) hide();
}

export function queueLength() {
  return queue.length;
}

export function queueStatusMessage() {
  return queueMessage();
}
